import React, { useEffect, useRef } from 'react';
import {
  FaClock,
  FaShieldAlt,
  FaMapMarkerAlt,
  FaUtensils,
  FaCog,
  FaCreditCard,
  FaMobile,
  FaLeaf,
} from 'react-icons/fa';

const Features = () => {
  const ref = useRef(null);

  useEffect(() => {
    const io = new IntersectionObserver(
      (e) =>
        e.forEach((x) => x.isIntersecting && x.target.classList.add('show')),
      { threshold: 0.2 }
    );
    ref.current?.querySelectorAll('.reveal').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const features = [
    {
      icon: <FaUtensils />,
      title: 'All‑in‑One Food Delivery',
      desc: 'From South Indian breakfasts to North Indian thalis, snacks, juices, and more',
    },
    {
      icon: <FaLeaf />,
      title: 'Fresh Vegetable Delivery',
      desc: 'Hand‑picked, farm‑fresh vegetables delivered directly to your home',
    },
    {
      icon: <FaClock />,
      title: 'Fast & Reliable Delivery',
      desc: 'Hot meals and fresh veggies delivered at the right time, every time',
    },
    {
      icon: <FaMobile />,
      title: 'Easy Ordering',
      desc: 'User‑friendly interface lets you order meals or veggies in just a few taps',
    },
    {
      icon: <FaCog />,
      title: 'Customizable Orders',
      desc: 'Choose spice levels, add‑ons, and swap items according to your taste',
    },
    {
      icon: <FaCreditCard />,
      title: 'Multiple Payment Options',
      desc: 'Pay securely using UPI, cards, mobile wallets, or cash on delivery',
    },
    {
      icon: <FaMapMarkerAlt />,
      title: 'Real‑Time Order Tracking',
      desc: 'Track your food or veggie delivery live from kitchen/market to your home',
    },
    {
      icon: <FaShieldAlt />,
      title: 'Fresh Ingredients & Hygiene',
      desc: 'We use the highest quality ingredients with strict hygiene standards',
    },
  ];

  return (
    <section id="features" className="section">
      <div className="container" ref={ref}>
        <div className="text-center reveal">
          <h2 className="title">Key Features</h2>
          <p className="subtitle">
            Discover what makes Mealzy Foods the perfect choice for your daily
            food and vegetable needs
          </p>
        </div>

        <div className="features-grid">
          {features.map((feat, i) => (
            <div key={i} className="feature-card reveal">
              <div className="feature-icon">{feat.icon}</div>
              <div className="feature-content">
                <h3 className="feature-title">{feat.title}</h3>
                <p className="feature-desc">{feat.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .features-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
          margin-top: 3rem;
        }

        @media (min-width: 540px) {
          .features-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 2rem;
          }
        }

        @media (min-width: 1024px) {
          .features-grid {
            grid-template-columns: repeat(4, 1fr);
            gap: 2.5rem;
            margin-top: 4rem;
          }
        }

        .feature-card {
          background: var(--surface);
          border: 1px solid var(--glass-bd);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          padding: 2rem 1.5rem;
          text-align: center;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }

        @media (max-width: 539px) {
          .feature-card {
            padding: 1.5rem;
          }
        }

        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: var(--shadow-xl);
        }

        .feature-icon {
          width: 4rem;
          height: 4rem;
          border-radius: 1rem;
          background: var(--grad);
          display: grid;
          place-items: center;
          color: #0a0a0a;
          font-size: 1.25rem;
          margin: 0 auto 1.5rem;
          box-shadow: 0 12px 30px rgba(255,140,70,.3);
          transition: transform 0.3s ease;
        }

        @media (max-width: 539px) {
          .feature-icon {
            width: 3.5rem;
            height: 3.5rem;
            font-size: 1.125rem;
          }
        }

        .feature-card:hover .feature-icon {
          transform: scale(1.1);
        }

        .feature-title {
          font-size: clamp(1rem, 2.5vw, 1.125rem);
          font-weight: 700;
          margin-bottom: 0.75rem;
          color: var(--text);
          line-height: 1.3;
        }

        .feature-desc {
          color: var(--muted);
          font-size: clamp(0.875rem, 2vw, 1rem);
          line-height: 1.5;
        }
      `}</style>
    </section>
  );
};

export default Features;
